import * as React from "react";
import { Button } from "./button";
import { Card } from "./card";
import { Input } from "./input";
import { Label } from "./label";
import { StepsIndicator } from "./steps-indicator";
import { EmailNotice } from "./email-notice";
import { OtpInput } from "./otp-input";
import { useAuth, type Me } from "../hooks/use-auth";
import { useToast } from "../hooks/use-toast";
import { ApiError } from "../lib/api-client";
import { getRememberedEmail, setRememberedEmail } from "../lib/storage";
import { cn, formatTimer } from "../lib/utils";

export interface LoginPageProps {
  appName: string;
  onLoggedIn: (me: Me) => void;
}

type Step = 1 | 2;
type Step1Err = "" | "email" | "unverified";

const OTP_TTL_SEC = 300;
const RESEND_COOLDOWN_SEC = 30;

export function LoginPage({ appName, onLoggedIn }: LoginPageProps) {
  const auth = useAuth();
  const toast = useToast();

  const [step, setStep] = React.useState<Step>(1);

  const [email, setEmail] = React.useState(() => getRememberedEmail() ?? "");
  const [rememberEmail, setRememberEmail] = React.useState(
    () => getRememberedEmail() !== null,
  );
  const [step1Error, setStep1Error] = React.useState<Step1Err>("");
  const [step1Loading, setStep1Loading] = React.useState(false);

  const [pendingToken, setPendingToken] = React.useState<string | null>(null);
  const [otp, setOtp] = React.useState("");
  const [otpError, setOtpError] = React.useState(false);
  const [step2Loading, setStep2Loading] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [timer, setTimer] = React.useState(OTP_TTL_SEC);
  const [resendCooldown, setResendCooldown] = React.useState(RESEND_COOLDOWN_SEC);

  React.useEffect(() => {
    if (step !== 2) return;
    const id = setInterval(() => {
      setTimer((t) => Math.max(0, t - 1));
      setResendCooldown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [step]);

  const expiredOnce = React.useRef(false);
  React.useEffect(() => {
    if (step === 2 && timer === 0 && !expiredOnce.current) {
      expiredOnce.current = true;
      toast.warn("인증코드가 만료되었습니다. 재발송하세요");
    }
    if (step === 2 && timer > 0) expiredOnce.current = false;
  }, [step, timer, toast]);

  async function handleStep1(e: React.FormEvent) {
    e.preventDefault();
    setStep1Error("");
    if (!email.trim()) {
      setStep1Error("email");
      return;
    }
    setStep1Loading(true);
    const normalized = email.trim().toLowerCase();
    try {
      const { pendingToken: pt } = await auth.login(normalized);
      setRememberedEmail(rememberEmail ? normalized : null);
      setPendingToken(pt);
      setStep(2);
      setOtp("");
      setOtpError(false);
      setTimer(OTP_TTL_SEC);
      setResendCooldown(RESEND_COOLDOWN_SEC);
      toast.info("인증코드가 이메일로 발송되었습니다");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setStep1Error("unverified");
      } else if (e instanceof ApiError && e.status === 429) {
        toast.error("요청이 너무 잦습니다. 잠시 후 다시 시도하세요");
      } else {
        toast.error("로그인 실패. 다시 시도해주세요");
      }
    } finally {
      setStep1Loading(false);
    }
  }

  async function submitVerify(code: string) {
    if (code.length < 8) {
      toast.warn("8자리 인증코드를 모두 입력해주세요");
      return;
    }
    if (!pendingToken) return;
    setStep2Loading(true);
    setOtpError(false);
    try {
      const me = await auth.verify(pendingToken, code);
      toast.success("인증 완료");
      setTimeout(() => onLoggedIn(me), 300);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        toast.error("요청이 너무 잦습니다. 잠시 후 다시 시도하세요");
      } else {
        setOtpError(true);
        toast.error("인증코드가 올바르지 않습니다");
        setTimeout(() => {
          setOtp("");
          setOtpError(false);
        }, 1200);
      }
    } finally {
      setStep2Loading(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      const { pendingToken: pt } = await auth.login(
        email.trim().toLowerCase(),
      );
      setPendingToken(pt);
      setOtp("");
      setOtpError(false);
      setTimer(OTP_TTL_SEC);
      setResendCooldown(RESEND_COOLDOWN_SEC);
      toast.info("인증코드를 재발송했습니다");
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) {
        toast.error("요청이 너무 잦습니다. 잠시 후 다시 시도하세요");
      } else {
        toast.error("재발송 실패");
      }
    } finally {
      setResendLoading(false);
    }
  }

  function back() {
    setStep(1);
    setPendingToken(null);
    setOtp("");
    setOtpError(false);
    setTimer(OTP_TTL_SEC);
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-canvas p-6">
      <div className="w-[380px]">
        <div className="flex items-center gap-2 mb-7">
          <div className="w-9 h-9 rounded-base bg-primary flex items-center justify-center">
            <img src="/logo.png" alt="" width={20} height={20} />
          </div>
          <span className="text-base font-semibold tracking-tight">{appName}</span>
        </div>

        <StepsIndicator steps={["아이디 확인", "이메일 인증"]} current={step} />

        <Card className="p-8">
          {step === 1 ? (
            <form onSubmit={handleStep1}>
              <p className="text-[18px] font-semibold tracking-tight mb-1.5">로그인</p>
              <p className="text-[13px] text-ink-muted mb-6 leading-relaxed">
                등록된 이메일을 입력하세요.
                <br />
                해당 이메일로 8자리 인증코드를 발송합니다.
              </p>

              <div className="mb-4">
                <Label htmlFor="login-email">이메일</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일 입력"
                  autoComplete="username"
                  error={step1Error === "email" || step1Error === "unverified"}
                />
                {step1Error === "email" && (
                  <p className="text-[11px] text-danger mt-1">이메일을 입력해주세요.</p>
                )}
                {step1Error === "unverified" && (
                  <p className="text-[11px] text-danger mt-1">
                    등록된 계정이 아닙니다. 담당자에게 문의하세요.
                  </p>
                )}
              </div>

              <label className="flex items-center gap-2 text-[12px] text-ink-muted cursor-pointer select-none mb-3">
                <input
                  type="checkbox"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  className="w-3.5 h-3.5 accent-primary"
                />
                이메일 기억하기
              </label>

              <Button type="submit" className="w-full mt-2" loading={step1Loading}>
                인증코드 발송
              </Button>
              <p className="text-[11px] text-ink-subtle text-center mt-3">
                등록된 계정이 없으시면 담당자에게 문의하세요
              </p>
            </form>
          ) : (
            <div>
              <button
                type="button"
                onClick={back}
                className="flex items-center gap-1 text-[12px] text-ink-subtle hover:text-ink-muted mb-4"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                이전으로
              </button>
              <p className="text-[18px] font-semibold tracking-tight mb-1.5">이메일 인증</p>
              <p className="text-[13px] text-ink-muted mb-6 leading-relaxed">
                아래 이메일로 발송된 8자리 인증코드를 입력해주세요.
              </p>

              <EmailNotice email={email} />

              <OtpInput
                length={8}
                charset="alphanumeric"
                value={otp}
                onChange={setOtp}
                error={otpError}
                autoFocus
              />
              {otpError && (
                <p className="text-[11px] text-danger text-center mt-1">
                  인증코드가 올바르지 않습니다. 다시 확인해주세요.
                </p>
              )}

              <div className="flex items-center justify-between mt-3">
                <span
                  className={cn(
                    "text-[12px] font-semibold",
                    timer <= 60 ? "text-danger" : "text-ink-subtle",
                  )}
                >
                  남은 시간 <b>{timer === 0 ? "만료" : formatTimer(timer)}</b>
                </span>
                <button
                  type="button"
                  onClick={() => void handleResend()}
                  disabled={resendCooldown > 0 || resendLoading || step2Loading}
                  className="text-[12px] text-primary underline underline-offset-2 hover:text-primary-hover disabled:text-ink-subtle disabled:no-underline disabled:cursor-default"
                >
                  {resendLoading ? "재발송 중..." : "재발송"}
                </button>
              </div>

              <Button
                type="button"
                onClick={() => void submitVerify(otp)}
                className="w-full mt-5"
                loading={step2Loading}
                disabled={otp.length < 8 || resendLoading}
              >
                인증 완료
              </Button>
              <p className="text-[11px] text-ink-subtle text-center mt-3">
                인증코드가 오지 않으면 스팸함을 확인하거나
                <br />
                담당자에게 이메일 등록을 요청하세요
              </p>
            </div>
          )}
        </Card>
      </div>
    </main>
  );
}
